import { useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/PageHeader";
import { useDashboardPage } from "@/hooks/useDashboardPage";
import {
  DASHBOARD_CHART_GRADIENT_ID,
  DASHBOARD_CHART_PRIMARY,
  DASHBOARD_TIME_RANGES,
  DashboardTimeRange,
} from "@/constants/dashboard";

export function Dashboard() {
  const { t } = useTranslation();
  const {
    projects,
    stats,
    loading,
    timeRange,
    setTimeRange,
    selectedProject,
    setSelectedProject,
    totalApiKeys,
    totalMetrics,
  } = useDashboardPage();

  const summaryCards = useMemo(
    () => [
      {
        icon: <TrendingUpIcon />,
        label: t("dashboard.totalRequests"),
        value: stats?.totalRequests ?? 0,
        color: "#7C4DFF",
      },
      {
        icon: <PeopleIcon />,
        label: t("dashboard.uniqueUsers"),
        value: stats?.uniqueFingerprints ?? 0,
        color: "#FF4081",
      },
      {
        icon: <BarChartIcon />,
        label: t("dashboard.metricsCount"),
        value: totalMetrics,
        color: "#4CAF50",
      },
      {
        icon: <VpnKeyIcon />,
        label: t("dashboard.apiKeysCount"),
        value: totalApiKeys,
        color: "#FF9800",
      },
    ],
    [stats, t, totalApiKeys, totalMetrics],
  );

  return (
    <Box>
      <PageHeader
        title={t("dashboard.title")}
        subtitle={t("dashboard.subtitle")}
        align="flex-start"
        actions={
          <>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t("dashboard.selectProject")}</InputLabel>
              <Select
                value={selectedProject}
                label={t("dashboard.selectProject")}
                onChange={(e) => setSelectedProject(e.target.value)}
              >
                <MenuItem value="">{t("dashboard.allProjects")}</MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <ToggleButtonGroup
              value={timeRange}
              exclusive
              onChange={(_, v: DashboardTimeRange | null) =>
                v && setTimeRange(v)
              }
              size="small"
            >
              {DASHBOARD_TIME_RANGES.map((r) => (
                <ToggleButton key={r} value={r}>
                  {t(`dashboard.timeRange.${r}`)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </>
        }
      />
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {summaryCards.map((card, idx) => (
          <Grid size={{ xs: 6, md: 3 }} key={idx}>
            <Card>
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2.5,
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: `${card.color}18`,
                    color: card.color,
                    display: "flex",
                  }}
                >
                  {card.icon}
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {card.label}
                  </Typography>
                  {loading ? (
                    <Skeleton width={60} height={32} />
                  ) : (
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                      }}
                    >
                      {card.value.toLocaleString()}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("dashboard.requestsVolume")}
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={300} />
          ) : stats?.timeSeries && stats.timeSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={stats.timeSeries}>
                <defs>
                  <linearGradient
                    id={DASHBOARD_CHART_GRADIENT_ID}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={DASHBOARD_CHART_PRIMARY}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="95%"
                      stopColor={DASHBOARD_CHART_PRIMARY}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke={DASHBOARD_CHART_PRIMARY}
                  strokeWidth={2}
                  fill={`url(#${DASHBOARD_CHART_GRADIENT_ID})`}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <Typography
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("dashboard.noData")}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
